import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { X, AlertCircle, CheckCircle, Clock, Zap, Cpu, Settings, Sparkles, Code, Database, Brain } from 'lucide-react';

const AdvancedAIProgress = ({ 
  isVisible, 
  onCancel, 
  onRetry,
  prompt = '',
  selectedModel = 'Unknown Model',
  requestType = 'chat'
}) => {
  // Map model IDs to display names
  const getModelDisplayName = (modelId) => {
    const modelNames = {
      'claude-3-5-haiku-20241022': 'Claude 3.5 Haiku',
      'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet', 
      'claude-3-7-sonnet-20250219': 'Claude 3.7 Sonnet',
      'claude-sonnet-4-20250514': 'Claude 4 Sonnet',
      'claude-3-opus-20240229': 'Claude 3 Opus',
      'claude-opus-4-20250514': 'Claude 4 Opus'
    };
    return modelNames[modelId] || modelId || 'Unknown Model';
  };

  const displayModelName = getModelDisplayName(selectedModel);

  const [currentStage, setCurrentStage] = useState('initializing');
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Initializing AI request...');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isStuck, setIsStuck] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showDetailedLogs, setShowDetailedLogs] = useState(false);
  
  const intervalRef = useRef(null);
  const stuckTimeoutRef = useRef(null);

  // AI Processing stages with realistic timings and messages
  const stages = {
    chat: [
      { id: 'initializing', label: 'Initializing Request', duration: 1000, progress: 5 },
      { id: 'analyzing', label: 'Analyzing Your Message', duration: 3000, progress: 25 },
      { id: 'thinking', label: 'AI Reasoning & Processing', duration: 8000, progress: 70 },
      { id: 'generating', label: 'Generating Response', duration: 4000, progress: 95 },
      { id: 'finalizing', label: 'Finalizing Output', duration: 1000, progress: 100 }
    ],
    code: [
      { id: 'initializing', label: 'Preparing Code Generation', duration: 2000, progress: 5 },
      { id: 'analyzing', label: 'Understanding Requirements', duration: 4000, progress: 20 },
      { id: 'architecting', label: 'Designing Architecture', duration: 6000, progress: 40 },
      { id: 'coding', label: 'Writing Production Code', duration: 15000, progress: 80 },
      { id: 'optimizing', label: 'Optimizing & Testing', duration: 3000, progress: 95 },
      { id: 'finalizing', label: 'Packaging for Deployment', duration: 2000, progress: 100 }
    ]
  };

  const currentStages = stages[requestType] || stages.chat;

  // Get stage icon
  const getStageIcon = (stageId) => {
    const icons = {
      initializing: Settings,
      analyzing: Brain,
      thinking: Cpu,
      generating: Sparkles,
      finalizing: CheckCircle,
      architecting: Database,
      coding: Code,
      optimizing: Zap
    };
    return icons[stageId] || Clock;
  };

  // Add realistic processing messages
  const getStageMessages = (stageId, model) => {
    const messages = {
      initializing: [
        'Setting up AI infrastructure...',
        'Loading model parameters...',
        'Preparing secure connection...'
      ],
      analyzing: [
        'Understanding your request...',
        'Analyzing context and requirements...',
        'Processing natural language input...',
        'Identifying key objectives...'
      ],
      thinking: [
        `${model} is reasoning through the problem...`,
        'Considering multiple approaches...',
        'Evaluating best practices...',
        'Cross-referencing knowledge base...'
      ],
      generating: [
        'Crafting the perfect response...',
        'Structuring output format...',
        'Adding final touches...',
        'Quality checking response...'
      ],
      finalizing: [
        'Preparing final output...',
        'Running final validations...',
        'Almost ready!'
      ],
      architecting: [
        'Designing system architecture...',
        'Planning component structure...',
        'Defining data models...',
        'Setting up best practices...'
      ],
      coding: [
        'Writing production-ready code...',
        'Implementing core functionality...',
        'Adding error handling...',
        'Creating responsive UI components...',
        'Integrating APIs and services...',
        'Building advanced features...'
      ],
      optimizing: [
        'Optimizing performance...',
        'Testing functionality...',
        'Checking code quality...',
        'Validating best practices...'
      ]
    };
    return messages[stageId] || ['Processing...'];
  };

  // Add log entry
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-10), { timestamp, message, type }]);
  };

  // Progress simulation with realistic timing
  useEffect(() => {
    if (!isVisible) return;

    let stageIndex = 0;
    const startTime = Date.now();

    // Reset state
    setProgress(0);
    setCurrentStage('initializing');
    setIsStuck(false);
    setElapsedTime(0);
    setLogs([]);

    addLog('🚀 Starting AI processing...');
    addLog(`📝 Request: "${prompt.slice(0, 50)}${prompt.length > 50 ? '...' : ''}"`);
    addLog(`🧠 Model: ${selectedModel}`);

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      setElapsedTime(Math.floor(elapsed / 1000));

      if (stageIndex >= currentStages.length) {
        clearInterval(intervalRef.current);
        return;
      }

      const currentStageData = currentStages[stageIndex];
      const stageElapsed = elapsed - currentStages.slice(0, stageIndex).reduce((acc, stage) => acc + stage.duration, 0);
      const stageProgressPercent = Math.min(stageElapsed / currentStageData.duration, 1);

      // Update stage if completed
      if (stageProgressPercent >= 1) {
        addLog(`✅ Completed: ${currentStageData.label}`);
        stageIndex++;
        if (stageIndex < currentStages.length) {
          setCurrentStage(currentStages[stageIndex].id);
          addLog(`🔄 Starting: ${currentStages[stageIndex].label}`);
        }
      } else {
        // Update progress within current stage
        const prevProgress = stageIndex > 0 ? currentStages[stageIndex - 1].progress : 0;
        const stageProgressRange = currentStageData.progress - prevProgress;
        const newProgress = prevProgress + (stageProgressRange * stageProgressPercent);
        setProgress(newProgress);

        // Update status message with variety
        const messages = getStageMessages(currentStageData.id, selectedModel);
        const messageIndex = Math.floor((stageProgressPercent * messages.length)) % messages.length;
        setStatusMessage(messages[messageIndex]);
      }

      // Check if stuck (taking too long) - increased threshold and improved message
      if (elapsed > 120000) { // 2 minutes (increased from 30 seconds)
        setIsStuck(true);
        addLog('⏳ AI is working on your complex request...', 'info');
      }
    };

    intervalRef.current = setInterval(updateProgress, 300);

    // Set up stuck detection - increased timeout
    stuckTimeoutRef.current = setTimeout(() => {
      setIsStuck(true);
      addLog('⏳ Processing complex AI request - please wait...', 'info');
    }, 180000); // 3 minutes (increased from 25 seconds)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (stuckTimeoutRef.current) clearTimeout(stuckTimeoutRef.current);
    };
  }, [isVisible, prompt, selectedModel, requestType, currentStages]);

  // Force immediate DOM update when modal becomes visible
  useLayoutEffect(() => {
    if (isVisible) {
      // Force a synchronous DOM update to ensure modal appears immediately
      console.log('🎬 LAYOUT EFFECT: Modal visibility set to true - forcing immediate render');
      
      // This runs synchronously before the browser paints, ensuring immediate visibility
      const modalElement = document.querySelector('[role="dialog"]');
      if (modalElement) {
        console.log('✅ LAYOUT EFFECT: Modal element found in DOM immediately');
      } else {
        console.log('⚠️ LAYOUT EFFECT: Modal element not yet in DOM');
      }
    }
  }, [isVisible]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
  };

  // Always render the modal but control visibility with CSS and z-index
  return (
    <div 
      className={`fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 transition-all duration-300 ${isVisible ? 'z-50 opacity-100' : 'z-[-1] opacity-0 pointer-events-none'}`}
      role="dialog" 
      aria-modal="true" 
      aria-labelledby="ai-progress-title"
      style={{
        display: 'flex', // Always in DOM for immediate visibility
        visibility: isVisible ? 'visible' : 'hidden'
      }}
    >
      <div className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Cpu className="h-6 w-6 text-purple-400" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            </div>
            <div>
              <h3 id="ai-progress-title" className="text-lg font-semibold text-white">TokenOS AI Processing</h3>
              <p className="text-sm text-gray-400">Advanced AI • {elapsedTime}s elapsed</p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 text-gray-400 hover:text-white hover:bg-red-500/20 rounded-lg transition-colors"
            title="Cancel AI Request"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Section */}
        <div className="p-6 space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-300">Progress</span>
              <span className="text-purple-400 font-medium">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-violet-500 transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Current Status */}
          <div className="flex items-center space-x-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="relative">
              {React.createElement(getStageIcon(currentStage), {
                className: "h-5 w-5 text-purple-400 animate-pulse"
              })}
            </div>
            <div className="flex-1">
              <p className="text-white font-medium">{statusMessage}</p>
              <p className="text-sm text-gray-400 capitalize">{currentStage.replace(/([A-Z])/g, ' $1')}</p>
            </div>
          </div>

          {/* Model & Type Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-gray-800/30 rounded-lg">
              <p className="text-gray-400">Model</p>
              <p className="text-white font-medium">{displayModelName}</p>
            </div>
            <div className="p-3 bg-gray-800/30 rounded-lg">
              <p className="text-gray-400">Type</p>
              <p className="text-white font-medium capitalize">{requestType}</p>
            </div>
          </div>

          {/* Warning for stuck requests */}
          {isStuck && (
            <div className="p-4 bg-yellow-900/30 border border-yellow-500/30 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-yellow-300 font-medium">Request Taking Longer Than Expected</p>
                  <p className="text-yellow-200/80 text-sm mt-1">
                    The AI is processing a complex request. You can wait for completion or cancel and try a simpler request.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Activity Logs Toggle */}
          <button
            onClick={() => setShowDetailedLogs(!showDetailedLogs)}
            className="w-full text-left p-3 text-sm text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
          >
            {showDetailedLogs ? '▼' : '▶'} View Activity Logs ({logs.length})
          </button>

          {/* Detailed Logs */}
          {showDetailedLogs && (
            <div className="max-h-32 overflow-y-auto bg-black/50 rounded-lg p-3 space-y-1">
              {logs.map((log, index) => (
                <div key={index} className="text-xs text-gray-300 font-mono">
                  <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 p-6 border-t border-gray-700">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
          >
            Cancel Request
          </button>
          {isStuck && onRetry && (
            <button
              onClick={handleRetry}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
            >
              Retry Request
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedAIProgress;